/* eslint-disable react-i18n/no-dynamic-translation-keys */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MediaAssetsLabStore, withRefluxStore } from "../../../../../Stores";
import logger from "../../../../../utils/LoggerFrontend.js";
import { Group } from "../../../../../Components/Layout";
import { useTranslation } from "react-i18next";
import * as SC from "../../AudioLibrary/AudioGenerator/AudioGenerator.styled";
import {
  DEFAULT_FORMALITIES,
  DEFAULT_GENERATION_ERROR,
  DEFAULT_JINGLES,
  DEFAULT_LANGUAGES,
  DEFAULT_SOUNDS,
  ERROR_MAPPING
} from "../../AudioLibrary/AudioGenerator/AudioGenerator.constants";
import LoadingIndicator from "react-loading-indicator";
import Actions from "../../../../../Actions.js";
import { ActionsMediaAssetsLab } from "../../../../../Actions/ActionsMediaAssetsLab/ActionsMediaAssetsLab.js";
import { Tooltip } from "../../../../../Components/Overlays";
import { Button } from "../../../../../Components/Button";
import { SectionHeader, SectionLabel, TextEditorWrapper } from "./AudioEditor.styled.js";
import { Upload } from "../../../../../Components/Upload";
import CampaignCreateTeaserEditor from "../../Campaign__Create_TeaserEditor/Campaign__Create_TeaserEditor.jsx";
import { SelectInput } from "../../../../../Components/Form";

const limit = 500;

const AudioEditor = ({
  isRegeneratingLabText,
  campaignId,
  regenerateText = () => {},
  // Store
  previewInputProps,
  autoGeneratedData,
  organization
}) => {
  const { t } = useTranslation();
  const [prevValues, setPrevValues] = useState({
    url: "",
    text: "",
    voice: {},
    music: {},
    language: {}
  });

  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);

  const audioRef = useRef(null);

  useEffect(() => {
    const audioRefCopy = audioRef;
    return function stopAudio() {
      if (audioRefCopy?.current) {
        audioRefCopy.current.pause();
      }
    };
  }, []);

  const [data, setData] = useState({
    url: previewInputProps.audioUrl,
    text: previewInputProps.text,
    voice: DEFAULT_SOUNDS[0],
    music: DEFAULT_JINGLES[0],
    language: DEFAULT_LANGUAGES[0],
    formality: DEFAULT_FORMALITIES[0]
  });

  useEffect(() => {
    if (autoGeneratedData) {
      setData((prevState) => ({
        ...prevState,
        text: autoGeneratedData
      }));
    }
  }, [autoGeneratedData]);

  const handleChangeElement = useCallback((updates) => {
    ActionsMediaAssetsLab.changeLabProps({
      key: "autoGeneratedData",
      value: Object.values(updates)[0] || null
    });
  }, []);

  const handleChangeOption = (id, value) => {
    setData((prevState) => ({
      ...prevState,
      [id]: value
    }));
  };

  const isGenerationDisabled = useMemo(() => {
    const isTextEmpty = !data.text || data.text.trim() === "";
    const isUnchanged =
      data.url === prevValues.url &&
      data.music?.label === prevValues.music?.label &&
      data.voice?.label === prevValues.voice?.label &&
      data.language?.label === prevValues.language?.label &&
      data.text === prevValues.text;

    return (
      isTextEmpty ||
      isUnchanged ||
      isGeneratingAudio ||
      isRegeneratingLabText ||
      data.text.length > limit
    );
  }, [data, prevValues, isGeneratingAudio, isRegeneratingLabText]);

  const onAudioUploaded = useCallback(({ url }) => {
    ActionsMediaAssetsLab.changePreviewInputProps({ key: "audioUrl", value: url });
    ActionsMediaAssetsLab.changeTemplateInputProps({ key: "audioUrl", value: url });
  }, []);

  const cloudinaryContext = useMemo(() => {
    let context = `orgaId=${organization?.id}|source=spotify-audio-generator`;

    return campaignId ? (context += `|campaignId=${campaignId}`) : context;
  }, [organization?.id, campaignId]);

  const onGenerateClip = useCallback(() => {
    const ref = Actions.generateAudioClip.completed.listen((result) => {
      logger.log("AudioLibrary:geneateAudio", "completed", result);

      const clipUrl = result?.payload?.url;
      const durationInSeconds =
        result?.payload?.duration != null ? result.payload.duration / 1000 : 0;

      ActionsMediaAssetsLab.changePreviewInputProps({ key: "audioUrl", value: clipUrl });
      ActionsMediaAssetsLab.changePreviewInputProps({ key: "duration", value: durationInSeconds });

      ActionsMediaAssetsLab.changeTemplateInputProps({ key: "audioUrl", value: clipUrl });
      ActionsMediaAssetsLab.changeTemplateInputProps({ key: "duration", value: durationInSeconds });
      setIsGeneratingAudio(false);

      setPrevValues({
        ...data,
        url: clipUrl
      });

      Actions.addNotification("Nice, dein neuer Spotify-Clip wurde erzeugt.");
      ref();
    });

    const refFailed = Actions.generateAudioClip.failed.listen((err) => {
      logger.log("error:AudioLibrary:geneateAudio", "failed", err);

      const message = ERROR_MAPPING[err?.meta?.message] || DEFAULT_GENERATION_ERROR;
      const translatedMessage = t(`${message}`);
      Actions.addNotification(translatedMessage, "error");

      setIsGeneratingAudio(false);
      refFailed();
    });
    setIsGeneratingAudio(true);

    Actions.generateAudioClip({
      orgaId: organization?.id,
      tbCampaignId: campaignId,
      text: data.text,
      background_music: data.music?.value,
      gender: data.voice?.value,
      language: data.language?.value,
      volume_music: 2,
      min_duration: 20000
    });
  }, [data]);

  const audioUrl = useMemo(() => {
    if (audioRef?.current) {
      audioRef.current.load();
      audioRef.current.play();
    }
    return previewInputProps.audioUrl;
  }, [previewInputProps.audioUrl]);

  const handleGenerate = useCallback(() => {
    regenerateText({ language: data.language.value, formality: data.formality.value });
  }, [data.formality.value, data.language.value]);

  return (
    <Group direction="column" style={{ maxWidth: "18rem" }}>
      <SectionLabel htmlFor="text">{t("AUDIO VERSION")}</SectionLabel>

      <TextEditorWrapper id="select-text">
        <CampaignCreateTeaserEditor
          showTeaserRecommendations={false}
          channel="spotify"
          updatePost={handleChangeElement}
          teaserText={data.text || ""}
          postDataKey="text"
          teaserTextStyle={SC.teaserTextStyle}
          placeholder="Aussage"
          limit={limit}
          editable
          smallButtons
          disableEmojis
        />
      </TextEditorWrapper>

      <Group>
        <SelectInput
          id="language"
          label={t("LANGUAGE")}
          value={data.language}
          type="select"
          options={DEFAULT_LANGUAGES}
          onChange={(val) => handleChangeOption("language", val)}
          menuPlacement="bottom"
          isSearchable={false}
        />
        <SelectInput
          id="voice"
          label={t("VOICE")}
          value={data.voice}
          type="select"
          options={DEFAULT_SOUNDS}
          onChange={(val) => handleChangeOption("voice", val)}
          menuPlacement="bottom"
          isSearchable={false}
        />
      </Group>
      <Group>
        <SelectInput
          id="music"
          label={t("MUSIC")}
          value={data.music}
          type="select"
          options={DEFAULT_JINGLES}
          onChange={(val) => handleChangeOption("music", val)}
          menuPlacement="bottom"
          isSearchable={false}
        />
        {data.language.label === "German" && (
          <SelectInput
            id="formal"
            label={t("FORMALITY")}
            value={data.formality}
            type="select"
            options={DEFAULT_FORMALITIES}
            onChange={(val) => handleChangeOption("formality", val)}
            menuPlacement="bottom"
            isSearchable={false}
          />
        )}
      </Group>

      <SectionHeader>{t("Actions")}</SectionHeader>
      <SC.GenerateAudioSection color="#c9cffe">
        <SC.ButtonRow>
          <Button
            onClick={handleGenerate}
            icon={!isRegeneratingLabText && <i className="fa fa-spotify"></i>}
            disabled={isRegeneratingLabText}
            variant="secondary"
            light
            style={{ width: "100%" }}
            text={
              isRegeneratingLabText ? (
                <SC.LoadingIndicatorWrapper>
                  <LoadingIndicator />
                </SC.LoadingIndicatorWrapper>
              ) : (
                t("Regenerate text")
              )
            }
          />

          <Tooltip label={t("Upload clip")}>
            <Upload
              handlePictureChanged={onAudioUploaded}
              showPreview={false}
              showLoading={true}
              allowImages={false}
              allowVideos={false}
              context={cloudinaryContext}
              fileTypes={["mp3"]}
              useUploadIcon
            />
          </Tooltip>
        </SC.ButtonRow>
        <Tooltip
          label={`${t("Make some changes to create a new clip.")} ${
            data.text.length > limit ? t("Text is too long.") : ""
          }`}
          enabled={isGenerationDisabled}
          containerStyles={{ width: "100%" }}
        >
          <Button
            disabled={isGenerationDisabled}
            onClick={onGenerateClip}
            icon={!isGeneratingAudio && <i className="fa fa-spotify"></i>}
            variant="tertiary"
            style={{ width: "100%" }}
            text={
              isGeneratingAudio ? (
                <SC.LoadingIndicatorWrapper>
                  <LoadingIndicator />
                </SC.LoadingIndicatorWrapper>
              ) : (
                t("Generate clip")
              )
            }
          />
        </Tooltip>
      </SC.GenerateAudioSection>

      <SC.TextSectionWrapper>
        <SectionLabel bottom htmlFor="select-preview">
          {t("PREVIEW")}
        </SectionLabel>
        <audio id="select-preview" style={{ width: "100%" }} controls ref={audioRef}>
          <track kind="captions" />
          <source src={audioUrl} type="audio/mp3" />
        </audio>
      </SC.TextSectionWrapper>
    </Group>
  );
};

export default withRefluxStore(
  AudioEditor,
  [MediaAssetsLabStore],
  ["previewInputProps", "organization", "autoGeneratedData"]
);
